import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import { getSubjectiveExams, createSubjectiveExam, updateSubjectiveExam, deleteSubjectiveExam } from "../services/subjective_exams";
import { getCourses, getTeacherCourses } from "../services/courses";
import { Plus, Trash2, Edit2, Loader2, Sparkles, BookOpen } from "lucide-react";
import toast, { Toaster } from 'react-hot-toast';
import { useNavigate } from "react-router-dom";

export default function TeacherSubjectiveExams() {
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [editModeId, setEditModeId] = useState(null);
  
  const defaultExamState = {
    title: "",
    course_id: "",
    duration_minutes: 60,
    total_marks: 100,
    questions: []
  };

  const [newExam, setNewExam] = useState(defaultExamState);

  const loadData = async () => {
    try {
      const [examsRes, coursesRes] = await Promise.all([
        getSubjectiveExams(),
        getTeacherCourses()
      ]);
      setExams(examsRes);
      setCourses(coursesRes.data || []);
    } catch (err) {
      toast.error("Failed to load exams");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const addQuestion = () => {
    setNewExam(prev => ({
      ...prev,
      questions: [
        ...prev.questions,
        { prompt: "", question_type: "long_answer", marks: 10 }
      ]
    }));
  };

  const removeQuestion = (index) => {
    setNewExam(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const handleQuestionChange = (index, field, value) => {
    setNewExam(prev => {
      const newQs = [...prev.questions];
      newQs[index][field] = value;
      return { ...prev, questions: newQs };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!newExam.title || newExam.questions.length === 0) {
      toast.error("Please add a title and at least one question");
      return;
    }
    
    try {
      const payload = {
        ...newExam,
        course_id: newExam.course_id ? parseInt(newExam.course_id) : null,
        duration_minutes: parseInt(newExam.duration_minutes) || 60,
        total_marks: parseFloat(newExam.total_marks) || 100,
        questions: newExam.questions.map(q => ({
          ...q,
          marks: parseFloat(q.marks) || 0
        }))
      };

      if (editModeId) {
        await updateSubjectiveExam(editModeId, payload);
        toast.success("Subjective Exam updated!");
      } else {
        await createSubjectiveExam(payload);
        toast.success("Subjective Exam created!");
      }
      
      setIsCreating(false);
      setEditModeId(null);
      setNewExam(defaultExamState);
      loadData();
    } catch (err) {
      toast.error(editModeId ? "Failed to update exam" : "Failed to create exam");
    }
  };

  const handleEdit = (exam) => {
    setNewExam({
      title: exam.title,
      course_id: exam.course_id || "",
      duration_minutes: exam.duration_minutes,
      total_marks: exam.total_marks,
      questions: exam.questions || []
    });
    setEditModeId(exam.id);
    setIsCreating(true);
  };

  const handleDelete = async (examId) => {
    if (!window.confirm("Are you sure you want to delete this exam?")) return;
    try {
      await deleteSubjectiveExam(examId);
      toast.success("Exam deleted successfully");
      loadData();
    } catch (err) {
      toast.error("Failed to delete exam");
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Toaster position="top-right" />
      <Sidebar role="teacher" />
      <div className="flex-1 overflow-y-auto p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800">Subjective Exams</h1>
            {!isCreating && (
              <button 
                onClick={() => {
                  setNewExam(defaultExamState);
                  setEditModeId(null);
                  setIsCreating(true);
                }}
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-indigo-700 transition"
              >
                <Plus size={20} /> Create New Exam
              </button>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center p-12"><Loader2 className="animate-spin text-indigo-600" size={48} /></div>
          ) : isCreating ? (
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h2 className="text-xl font-semibold mb-4">{editModeId ? "Edit Descriptive Exam" : "Create Descriptive Exam"}</h2>
              <form onSubmit={handleSave} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Exam Title</label>
                    <input 
                      required
                      value={newExam.title} 
                      onChange={e => setNewExam({...newExam, title: e.target.value})}
                      className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-indigo-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Course (Optional)</label>
                    <select 
                      value={newExam.course_id}
                      onChange={e => setNewExam({...newExam, course_id: e.target.value})}
                      className="w-full border rounded-lg p-2"
                    >
                      <option value="">Select a Course</option>
                      {courses.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Duration (Mins)</label>
                    <input 
                      type="number" required min="1"
                      value={newExam.duration_minutes} 
                      onChange={e => setNewExam({...newExam, duration_minutes: e.target.value})}
                      className="w-full border rounded-lg p-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Marks</label>
                    <input 
                      type="number" required min="1"
                      value={newExam.total_marks} 
                      onChange={e => setNewExam({...newExam, total_marks: e.target.value})}
                      className="w-full border rounded-lg p-2"
                    />
                  </div>
                </div>

                <div className="border-t pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-800">Questions</h3>
                    <button type="button" onClick={addQuestion} className="text-indigo-600 font-medium flex items-center gap-1 hover:text-indigo-800">
                      <Plus size={16} /> Add Question
                    </button>
                  </div>

                  {newExam.questions.map((q, i) => (
                    <div key={i} className="bg-gray-50 p-4 rounded-lg mb-4 border border-gray-200 relative">
                      <button type="button" onClick={() => removeQuestion(i)} className="absolute top-2 right-2 text-red-400 hover:text-red-600 transition" title="Delete Question">
                        <Trash2 size={20} />
                      </button>
                      <div className="space-y-4 pr-8">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Question Prompt</label>
                          <textarea 
                            required rows="3"
                            value={q.prompt}
                            onChange={e => handleQuestionChange(i, 'prompt', e.target.value)}
                            className="w-full border rounded-lg p-2"
                            placeholder="Type the question here..."
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                            <select 
                              value={q.question_type}
                              onChange={e => handleQuestionChange(i, 'question_type', e.target.value)}
                              className="w-full border rounded-lg p-2"
                            >
                              <option value="long_answer">Long Answer</option>
                              <option value="short_answer">Short Answer</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Marks</label>
                            <input 
                              type="number" required min="0" step="0.5"
                              value={q.marks}
                              onChange={e => handleQuestionChange(i, 'marks', e.target.value)}
                              className="w-full border rounded-lg p-2"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {newExam.questions.length === 0 && (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                      No questions added yet. Click "Add Question" to start.
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-6 border-t mt-8">
                  <button 
                    type="button" 
                    onClick={() => {
                      setIsCreating(false);
                      setEditModeId(null);
                      setNewExam(defaultExamState);
                    }} 
                    className="px-6 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium flex items-center gap-2"
                  >
                    {editModeId ? "Update Exam" : "Publish Exam"}
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {exams.map(exam => (
                <div key={exam.id} className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-800 line-clamp-1">{exam.title}</h3>
                      <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                        <BookOpen size={14} /> 
                        {exam.course_id ? courses.find(c => c.id === exam.course_id)?.title || 'Unknown Course' : 'No Course Linked'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => handleEdit(exam)}
                        className="text-gray-400 hover:text-indigo-600 p-1"
                        title="Edit Exam"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button 
                        onClick={() => handleDelete(exam.id)}
                        className="text-gray-400 hover:text-red-600 p-1"
                        title="Delete Exam"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mb-4 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                    <div>
                      <div className="font-semibold text-gray-900">{exam.duration_minutes}</div>
                      <div>Mins</div>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{exam.total_marks}</div>
                      <div>Marks</div>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{exam.questions?.length || 0}</div>
                      <div>Questions</div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => navigate(`/teacher-subjective-evaluations/${exam.id}`)}
                      className="flex-1 bg-indigo-50 text-indigo-700 py-2 rounded-lg font-medium text-sm hover:bg-indigo-100"
                    >
                      View Submissions
                    </button>
                  </div>
                </div>
              ))}
              {exams.length === 0 && (
                <div className="col-span-full text-center py-12 bg-white rounded-xl border border-dashed border-gray-300 text-gray-500">
                  <BookOpen size={48} className="mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium text-gray-900 mb-1">No Subjective Exams</h3>
                  <p>Create your first descriptive exam to evaluate students.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
